import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isPrerendering } from "../../utils/isPrerendering";
import MagTeamBoard from "../MagTeamBoard";

function readingProgress(readingEl) {
  const rect = readingEl.getBoundingClientRect();
  if (rect.height <= 0) return 0;
  const passed = window.innerHeight * 0.5 - rect.top;
  return Math.min(1, Math.max(0, passed / rect.height));
}

export default function AboutNarrative() {
  const sectionRef = useRef(null);
  const readingRef = useRef(null);
  const [enhanced, setEnhanced] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isPrerendering()) return undefined;

    setEnhanced(true);

    const desktopMq = window.matchMedia("(min-width: 1024px)");
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncFlags = () => {
      setIsDesktop(desktopMq.matches);
      setReduceMotion(motionMq.matches);
    };
    syncFlags();

    let frame = 0;
    const measure = () => {
      frame = 0;
      const readingEl = readingRef.current;
      if (!readingEl) return;
      const nextProgress = Math.round(readingProgress(readingEl) * 1000) / 1000;
      setProgress((prev) => (prev === nextProgress ? prev : nextProgress));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    desktopMq.addEventListener("change", syncFlags);
    motionMq.addEventListener("change", syncFlags);
    measure();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      desktopMq.removeEventListener("change", syncFlags);
      motionMq.removeEventListener("change", syncFlags);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const showHairline = enhanced && isDesktop && !reduceMotion;

  return (
    <section
      ref={sectionRef}
      className="relative bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
      aria-labelledby="about-built-heading about-mag-heading"
    >
      <div className="relative grid gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
        <div className="lg:col-span-5">
          <MagTeamBoard compact />
        </div>

        <div ref={readingRef} className="relative lg:col-span-7">
          {showHairline ? (
            <div
              className="pointer-events-none absolute bottom-0 left-0 top-0 hidden w-0.5 bg-gray-200 lg:block"
              aria-hidden="true"
            >
              <div className="w-full bg-gray-900" style={{ height: `${progress * 100}%` }} />
            </div>
          ) : null}

          <article id="about-block-built" className="lg:pl-8">
            <h2
              id="about-built-heading"
              className="max-w-3xl text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
            >
              Built for Manufacturing Automation
            </h2>
            <div className="mt-5 max-w-[65ch] space-y-4 text-base leading-7 text-gray-600">
              <p>
                Manufacturing Automation Group (MAG) helps manufacturers solve critical
                operational challenges such as labor shortages, rising costs, and complex production
                launches through practical automation solutions designed for the plant floor.
              </p>
              <p>
                Founded in Detroit in 2024, the team brings deep experience deploying AMR and
                industrial automation systems at scale within high-volume manufacturing environments.
                Material flow, operational constraints, and plant dynamics are evaluated inside
                customer facilities before automation is designed and integrated.
              </p>
              <p>
                Partnerships with OEMs, suppliers, and technology providers cover planning, launch, and
                support for <Link to="/products-services" discover="none" className="font-medium text-gray-900 underline-offset-4 hover:underline">AMR and AGV systems</Link> that improve
                material handling, increase efficiency, and strengthen production
                resilience.
              </p>
            </div>
          </article>

          <article id="about-block-mag" className="mt-12 sm:mt-16 lg:pl-8">
            <h2
              id="about-mag-heading"
              className="max-w-3xl text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
            >
              About MAG
            </h2>
            <div className="mt-5 max-w-[65ch] space-y-4 text-base leading-7 text-gray-600">
              <p>
                Manufacturing Automation Group (MAG) is a technology integration partner specializing
                in AGV and AMR systems. Backed by internal AI tools, solutions are designed, deployed, and supported
                to improve material flow, increase operational consistency, and bring
                stability to complex environments. Customers and technology
                partners stay in the work from early strategy and design through launch and long-term support. The team brings hands-on experience in high-volume production environments where uptime, execution, and reliability matter.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
